import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import SessionSettingApi from '../../../api/SessionSettingApi';

const SessionSettingManager = () => {
  const api = useMemo(() => new SessionSettingApi(), []);
  const [settings, setSettings] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [unitValues, setUnitValues] = useState({});
  const [configValues, setConfigValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  const visibleSettings = settings;

  useEffect(() => {
    const loadSettings = async () => {
      const response = await api.fetchSettings();
      const result = response?.data;
      console.log('result', result);
      if (result?.success) {
        setSettings(result.data || []);
        const initialValues = (result.data || []).reduce((acc, setting) => {
          const parsedConfig = parseConfig(setting.config);
          if (parsedConfig.datatype === 'multipicklist') {
            acc[setting.key] = parseMultiValue(setting.value);
          } else if (parsedConfig.datatype === 'checkbox') {
            acc[setting.key] = parseBoolean(setting.value);
          } else if (
            parsedConfig.datatype === 'picklist' &&
            Array.isArray(parsedConfig.picklist_values)
          ) {
            // Match stored value to picklist option case-insensitively (e.g. off → Off)
            const raw = setting.value;
            const match = parsedConfig.picklist_values.find(
              opt =>
                String(opt).toLowerCase() === String(raw || '').toLowerCase()
            );
            acc[setting.key] = match != null ? match : raw;
          } else {
            acc[setting.key] = setting.value;
          }
          return acc;
        }, {});
        const initialConfigs = (result.data || []).reduce((acc, setting) => {
          acc[setting.key] = parseConfig(setting.config);
          return acc;
        }, {});
        const initialUnits = (result.data || []).reduce((acc, setting) => {
          acc[setting.key] = setting.unit || '';
          return acc;
        }, {});
        setFormValues(initialValues);
        setUnitValues(initialUnits);
        setConfigValues(initialConfigs);
      }
    };

    loadSettings();
  }, [api]);

  const handleChange = (key, value) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
  };

  const handleUnitChange = (key, value) => {
    setUnitValues(prev => ({ ...prev, [key]: value }));
  };

  const parseConfig = config => {
    if (!config) {
      return { datatype: 'text', picklist_values: [], unit: null };
    }
    if (typeof config === 'object') {
      return {
        datatype: 'text',
        picklist_values: [],
        unit: null,
        ...config,
      };
    }
    try {
      return {
        datatype: 'text',
        picklist_values: [],
        unit: null,
        ...JSON.parse(config),
      };
    } catch (error) {
      return { datatype: 'text', picklist_values: [], unit: null };
    }
  };

  const parseMultiValue = value => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [String(parsed)];
    } catch (error) {
      return String(value)
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
  };

  const parseBoolean = value => {
    if (typeof value === 'boolean') return value;
    return String(value).toLowerCase() === 'true';
  };

  const renderValueInput = setting => {
    const config = configValues[setting.key] || { datatype: 'text' };
    const value = formValues[setting.key] ?? '';

    if (config.datatype === 'checkbox') {
      return (
        <Form.Check
          type="checkbox"
          checked={Boolean(value)}
          onChange={e => handleChange(setting.key, e.target.checked)}
        />
      );
    }

    if (config.datatype === 'picklist') {
      const options = Array.isArray(config.picklist_values)
        ? config.picklist_values
        : [];
      return (
        <Form.Select
          value={value}
          onChange={e => handleChange(setting.key, e.target.value)}
        >
          <option value="">Select</option>
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Select>
      );
    }

    if (config.datatype === 'multipicklist') {
      const options = Array.isArray(config.picklist_values)
        ? config.picklist_values
        : [];
      return (
        <Form.Select
          multiple
          value={Array.isArray(value) ? value : []}
          onChange={e =>
            handleChange(
              setting.key,
              Array.from(e.target.selectedOptions).map(opt => opt.value)
            )
          }
        >
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Select>
      );
    }

    const inputType = config.datatype === 'number' ? 'number' : 'text';
    return (
      <Form.Control
        type={inputType}
        value={value}
        onChange={e => handleChange(setting.key, e.target.value)}
      />
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    const hasEmpty = visibleSettings.some(
      setting => !formValues[setting.key]
    );
    if (hasEmpty) {
      setMessage('Please fill all values before saving.');
      setIsSaving(false);
      return;
    }
    const updates = visibleSettings.map(setting => {
      const config = configValues[setting.key] || { datatype: 'text' };
      let nextValue = formValues[setting.key];
      if (config.datatype === 'checkbox') {
        nextValue = String(Boolean(nextValue));
      }
      if (config.datatype === 'multipicklist') {
        nextValue = JSON.stringify(Array.isArray(nextValue) ? nextValue : []);
      }
      return {
        key: setting.key,
        value: nextValue === undefined ? '' : String(nextValue),
        unit: unitValues[setting.key] || null,
      };
    });

    const response = await api.updateSettings(updates);
    const result = response?.data;
    if (result?.success) {
      setSettings(result.data || []);
      setMessage('Settings updated successfully.');
    } else {
      setMessage(result?.errors || 'Failed to update settings.');
    }
    setIsSaving(false);
  };

  return (
    <div className="wrapper">
      <div id="content">
        <Card className="p-4">
          <h4 className="mb-4">Session Settings</h4>
          {visibleSettings.map(setting => (
            <Row className="align-items-center mb-3" key={setting.key}>
              <Col md={5}>
                <Form.Label className="mb-0">{setting.label}</Form.Label>
              </Col>
              <Col md={4}>
                {renderValueInput(setting)}
              </Col>
              <Col md={3}>
                {(configValues[setting.key]?.unit || '') === 'picklist' &&
                  unitValues[setting.key] ? (
                  <Form.Select
                    value={unitValues[setting.key]}
                    onChange={e =>
                      handleUnitChange(setting.key, e.target.value)
                    }
                  >
                    {(configValues[setting.key]?.unit_picklist_values || []).map(
                      unit => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      )
                    )}
                  </Form.Select>
                ) : unitValues[setting.key] ? (
                  <span>{unitValues[setting.key]}</span>
                ) : null}
              </Col>
            </Row>
          ))}
          {message && <div className="mt-2 text-muted">{message}</div>}
          <div className="mt-4">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SessionSettingManager;
